/* iceberg-ui */
import { Icon } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';

/* React */
import * as React from 'react';
import { Link } from 'react-router';

/* Shared */
import { TooltipQ } from '../Tooltips';

const ITEM_HEIGHT = 48;

const defaultFn = () => {};

/**
 * KebabMenu component that renders a dropdown menu.
 *
 * To add section titles to the menu, you will need to add an object to the options, of the type
 * { isParent: true, key: 'parent1', label: 'Parent 1' }
 *
 * @param {Object} props - The component props
 * @param {string} [props.data-testid='kebabMenu'] - Test ID for the component
 * @param {string} [props.data-trackid='kebabMenu'] - Tracking ID for analytics
 * @param {any} [props.defaultSelection] - Default selected option
 * @param {Array} props.options - The menu options array - must contain label, key, and value for actionable options, or label, key, and isParent: true for section titles. Can also include tooltip, disabled, hide, handler, and dataTrackid properties
 * @param {Function} props.onClick - Callback fired when an option is clicked
 * @param {Function} [props.onClose] - Callback fired when menu closes
 * @param {Object} [props.paperProps] - Props passed to the Menu's Paper component
 * @param {Object} [props.rest] - Additional props passed to the Menu component
 * @returns {JSX.Element} The KebabMenu component
 */
export const KebabMenu = ({
    'data-testid': dataTestId = 'kebabMenu',
    'data-trackid': dataTrackid = 'kebabMenu',
    defaultSelection,
    options,
    onClick,
    onClose = defaultFn,
    paperProps,
    ...rest
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        onClose();
    };

    const handleOptionClick = (value) => {
        onClick(value);
        handleClose();
    };

    const hasSections = options.some((option) => !!option.isParent);

    return (
        <>
            <IconButton
                aria-label="more"
                id="kebabMenuButton"
                aria-controls={open ? 'kebabMenu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                color="secondary"
                data-testid={dataTestId}
                data-trackid={dataTrackid}
            >
                <Icon size="13px" src="Menu" />
            </IconButton>
            <Menu
                id="kebabMenu"
                MenuListProps={{
                    'aria-labelledby': 'kebabMenuButton',
                    style: { padding: 0 }
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    'data-testid': 'kebabMenuPaper',
                    style: {
                        maxHeight: ITEM_HEIGHT * 4.5,
                        paddingBottom: '12px',
                        paddingLeft: hasSections ? 0 : '12px',
                        paddingRight: hasSections ? 0 : '12px',
                        paddingTop: hasSections ? 0 : '12px'
                    },
                    ...paperProps
                }}
                {...rest}
            >
                {options.map((option) => {
                    if (option.isParent && !option.hide) {
                        return (
                            <Typography
                                data-testid="kebabMenuSectionTitle"
                                fontWeight={600}
                                key={option.key}
                                sx={{ padding: '16px 16px 8px 16px' }}
                            >
                                {option.label}
                            </Typography>
                        );
                    }
                    if (!option.hide) {
                        return (
                            <KebabMenuItemWrapper
                                key={option.key}
                                options={option}
                            >
                                <MenuItem
                                    data-testid={option.key}
                                    data-trackid={
                                        option.dataTrackid ||
                                        `${dataTrackid}_actions_${option.value}`
                                    }
                                    disabled={option.disabled}
                                    selected={
                                        defaultSelection
                                            ? option.value === defaultSelection
                                            : false
                                    }
                                    onClick={() => {
                                        if ('handler' in option) {
                                            handleClose();
                                            option.handler();
                                        } else {
                                            handleOptionClick(option.value);
                                        }
                                    }}
                                    sx={{
                                        margin: hasSections ? '0 16px' : 0,
                                        padding: '4px 8px'
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        {...(hasSections
                                            ? { padding: '0 8px' }
                                            : {})}
                                    >
                                        {option.label}
                                        {option.tooltip && (
                                            <TooltipQ text={option.tooltip} />
                                        )}
                                    </Box>
                                </MenuItem>
                            </KebabMenuItemWrapper>
                        );
                    }
                    return null;
                })}
            </Menu>
        </>
    );
};

const KebabMenuItemWrapper = ({ options, children }) =>
    options.link ? (
        <Link
            data-trackid="kebabMenu_routeTo"
            rel="noopener noreferrer nofollow"
            {...options.link}
        >
            {children}
        </Link>
    ) : (
        children
    );
